"use client";

/**
 * QuickVariantSelector — Shop website variant selection dialog
 *
 * EXACT replica of the POS FashionProductCard variant modal.
 * Same design: edge-to-edge hero image, size grid, design grid, colour swatches,
 * stock indicator, quantity selector, and sticky bottom CTA.
 *
 * Stock is fetched live from Convex `stockBatches` via `getProductStock` so
 * the dialog always shows the true available quantity — not the stale value
 * carried on the product listing document.
 *
 * variantId is built with the same `buildSyntheticVariantId` used by the POS,
 * so stock-batch allocation matches at checkout confirmation.
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Minus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import Image from "next/image";
import { optimizeBunnyUrl } from "@/lib/utils/bunnyOptimizer";
import { photosForColour } from "@/lib/config/colourPhotos";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";

// ─── Colour fallback map (same as POS) ───────────────────────────────────────
const COLOR_MAP: Record<string, string> = {
  black: "#000000", white: "#FFFFFF", red: "#EF4444", blue: "#3B82F6",
  green: "#22C55E", yellow: "#EAB308", purple: "#A855F7", pink: "#EC4899",
  orange: "#F97316", brown: "#92400E", gray: "#6B7280", grey: "#6B7280",
  navy: "#1E3A5F", beige: "#F5F5DC", cream: "#FFFDD0", gold: "#FFD700",
  silver: "#C0C0C0", maroon: "#800000", teal: "#008080", coral: "#FF7F50",
  burgundy: "#800020", olive: "#808000", tan: "#D2B48C", khaki: "#C3B091",
  mint: "#98FF98", lavender: "#E6E6FA", peach: "#FFCBA4", turquoise: "#40E0D0",
  magenta: "#FF00FF", cyan: "#00FFFF", indigo: "#4B0082", charcoal: "#36454F",
  ivory: "#FFFFF0", rose: "#FF007F", wine: "#722F37", blush: "#DE5D83",
  nude: "#E3BC9A", rust: "#B7410E", mustard: "#FFDB58", lime: "#32CD32",
  emerald: "#50C878", sage: "#9DC183", sky: "#87CEEB", ocean: "#006994",
  cobalt: "#0047AB", royal: "#4169E1", sapphire: "#0F52BA", denim: "#1560BD",
  midnight: "#191970", plum: "#8E4585", lilac: "#C8A2C8", orchid: "#DA70D6",
  fuchsia: "#FF00FF", raspberry: "#E30B5C", cherry: "#DE3163", salmon: "#FA8072",
  apricot: "#FBCEB1", pumpkin: "#FF7518", amber: "#FFBF00", honey: "#EB9605",
  camel: "#C19A6B", mocha: "#967969", terracotta: "#E2725B", brick: "#CB4154",
  scarlet: "#FF2400", crimson: "#DC143C",
};
function getColorValue(name: string): string {
  return COLOR_MAP[name.toLowerCase().trim()] || "#808080";
}

/**
 * POS-identical synthetic variantId builder.
 * MUST match `buildSyntheticVariantId` in components/pos/FashionProductCard.tsx
 */
function buildSyntheticVariantId(
  colorId: string | undefined,
  designId: string | undefined,
  size: string | undefined,
): string | undefined {
  if (!colorId || !size) return undefined;
  const designPart = designId ? `-${designId}` : "";
  return `variant-${colorId}${designPart}-${size}`
    .replace(/[^\x20-\x7E]/g, "-")
    .replace(/-{2,}/g, "-");
}

/** Same sanitizer used by the backend (simplified JS copy). */
function sanitizeVariantId(id: string): string {
  return id.replace(/[^\x20-\x7E]/g, "-").replace(/-{2,}/g, "-");
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ColorObj {
  id?: string;
  name: string;
  hex: string;
  isMulticolor?: boolean;
  multicolorList?: string[];
  images?: any;
}

interface DesignObj {
  id: string;
  name: string;
}

interface QuickVariantSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    _id: string;
    name: string;
    slug?: string;
    price?: number;
    basePrice?: number;
    comparePrice?: number;
    images?: string[];
    totalStock?: number; // fallback only — live stock is fetched from Convex
    brandName?: string;
    brandSlug?: string;
    subcategoryName?: string;
    subcategorySlug?: string;
    productTypeName?: string;
    productTypeSlug?: string;
    fashionAttributes?: {
      colors?: ColorObj[];
      designs?: DesignObj[];
      sizes?: string[];
    };
  };
  onAddToCart: (quantity: number, variantId?: string, productData?: any) => void;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function QuickVariantSelector({
  isOpen,
  onClose,
  product,
  onAddToCart,
}: QuickVariantSelectorProps) {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedDesign, setSelectedDesign] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  // ── Live stock from Convex stockBatches ───────────────────────────────────
  // This fixes the "always shows 1" bug — the listing query carries a stale
  // product.stock field. We query stockBatches directly so the modal always
  // shows the true remaining quantity.
  const liveStockData = useQuery(
    api.products.shop.getProductStock,
    isOpen ? { productId: product._id as Id<"products"> } : "skip",
  );

  // Reset selections each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSize("");
      setSelectedColor("");
      setSelectedDesign("");
      setQuantity(1);
      setIsAdding(false);
    }
  }, [isOpen]);

  // ── Option lists ──────────────────────────────────────────────────────────
  const availableSizes: string[] = product.fashionAttributes?.sizes || [];
  const availableColors: ColorObj[] = (product.fashionAttributes?.colors || []).map((c) => ({
    id: c.id,
    name: c.name,
    hex: c.hex || getColorValue(c.name),
    isMulticolor: c.isMulticolor,
    multicolorList: c.multicolorList,
    images: c.images,
  }));
  const availableDesigns: DesignObj[] = product.fashionAttributes?.designs || [];

  // ── Auto-select single options ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    if (availableSizes.length === 1 && !selectedSize) setSelectedSize(availableSizes[0]);
    if (availableColors.length === 1 && !selectedColor) setSelectedColor(availableColors[0].name);
    if (availableDesigns.length === 1 && !selectedDesign) setSelectedDesign(availableDesigns[0].name);
  }, [isOpen, availableSizes, availableColors, availableDesigns, selectedSize, selectedColor, selectedDesign]);

  // ── Hero image ────────────────────────────────────────────────────────────
  // A colour's photos by the shared rule (lib/config/colourPhotos.ts): the
  // first colour shows the main photo, never its stale copy.
  const getDisplayImage = (): string => {
    if (selectedColor) {
      const colorObj = availableColors.find((c) => c.name === selectedColor);
      const photos = photosForColour(product.fashionAttributes, colorObj?.id);
      if (photos?.[0]) return photos[0];
    }
    return product.images?.[0] || "/images/placeholder-product.jpg";
  };

  // ── Stock resolution ──────────────────────────────────────────────────────
  // Priority:
  //   1. Per-variant stock from live stockBatches (most accurate)
  //   2. Total stock from live stockBatches
  //   3. Fallback to product.totalStock passed from card (may be stale)
  const getAvailableStock = (): number => {
    if (!liveStockData) {
      // Still loading — show the fallback so UX doesn't block
      return product.totalStock || 0;
    }

    const { totalStock, variantStock } = liveStockData;
    const hasVariantStock = Object.keys(variantStock).length > 0;

    if (hasVariantStock && selectedColor && selectedSize) {
      const colorObj = availableColors.find((c) => c.name === selectedColor);
      const designObj = availableDesigns.find((d) => d.name === selectedDesign);

      if (colorObj?.id) {
        const syntheticId = buildSyntheticVariantId(colorObj.id, designObj?.id, selectedSize);
        if (syntheticId) {
          const safeId = sanitizeVariantId(syntheticId);
          const variantQty = variantStock[safeId];
          if (variantQty !== undefined) return variantQty;
        }
      }
    }

    // No variant-specific stock → return the total (untagged product or no match yet)
    return totalStock;
  };

  const currentStock = getAvailableStock();

  // Clamp quantity when stock changes (e.g. after picking a variant)
  useEffect(() => {
    if (quantity > currentStock && currentStock > 0) {
      setQuantity(currentStock);
    }
  }, [currentStock, quantity]);

  // ── Validation ────────────────────────────────────────────────────────────
  const hasAllRequired =
    (availableSizes.length === 0 || !!selectedSize) &&
    (availableColors.length === 0 || !!selectedColor) &&
    (availableDesigns.length === 0 || !!selectedDesign);

  const canAdd = hasAllRequired && quantity > 0 && quantity <= currentStock && !isAdding;

  const getButtonLabel = (): string => {
    if (isAdding) return "Adding…";
    const missing: string[] = [];
    if (availableSizes.length > 0 && !selectedSize) missing.push("size");
    if (availableColors.length > 0 && !selectedColor) missing.push("colour");
    if (availableDesigns.length > 0 && !selectedDesign) missing.push("design");
    if (missing.length === 1) return `Select ${missing[0].charAt(0).toUpperCase()}${missing[0].slice(1)}`;
    if (missing.length === 2) return `Select ${missing[0]} & ${missing[1]}`;
    if (missing.length >= 3) return "Select size, colour & design";
    if (hasAllRequired && currentStock === 0) return "Out of Stock";
    return `Add ${quantity} to Cart`;
  };

  // ── Add to cart ───────────────────────────────────────────────────────────
  const handleAddToCart = async () => {
    if (!canAdd) {
      toast.error(getButtonLabel());
      return;
    }
    setIsAdding(true);
    try {
      const colorObj = selectedColor ? availableColors.find((c) => c.name === selectedColor) : undefined;
      const designObj = selectedDesign ? availableDesigns.find((d) => d.name === selectedDesign) : undefined;

      // Build POS-identical synthetic variantId
      let variantId: string | undefined;
      if (colorObj?.id) {
        variantId = buildSyntheticVariantId(colorObj.id, designObj?.id, selectedSize);
      }
      if (!variantId && (selectedColor || selectedSize)) {
        variantId = [selectedColor, selectedSize].filter(Boolean).join("-").toLowerCase().replace(/\s+/g, "-") || undefined;
      }

      onAddToCart(quantity, variantId, {
        _id: product._id,
        name: product.name,
        slug: product.slug,
        basePrice: product.basePrice ?? product.price,
        images: product.images,
        selectedVariants: {
          color: selectedColor || undefined,
          size: selectedSize || undefined,
          design: selectedDesign || undefined,
          colorHex: colorObj?.hex,
          isMulticolor: colorObj?.isMulticolor,
          multicolorList: colorObj?.multicolorList,
        },
      });
      onClose();
    } catch (err) {
      console.error("[QuickVariantSelector]", err);
      toast.error("Failed to add to cart");
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  const price = product.basePrice ?? product.price ?? 0;
  const isLoadingStock = liveStockData === undefined;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />

      {/* Modal — slides up from bottom on mobile, centred on desktop */}
      <div
        className="fixed inset-0 flex items-end sm:items-center justify-center pointer-events-none"
        style={{ zIndex: 9999 }}
      >
        <div
          className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl pointer-events-auto flex flex-col bg-white"
          style={{ maxHeight: "min(95vh, 95dvh)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-t-2xl min-h-0">

            {/* ── Hero image ─────────────────────────────────────────────── */}
            <div className="relative w-full overflow-hidden rounded-t-2xl">
              <div className="relative w-full" style={{ aspectRatio: "1/1", maxHeight: "min(40vh, 300px)" }}>
                <Image
                  src={optimizeBunnyUrl(getDisplayImage(), { width: 600 })}
                  alt={product.name}
                  fill
                  className="object-cover object-center transition-all duration-500"
                  unoptimized
                />
              </div>

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute right-3 top-3 z-10 rounded-full p-2 transition-all hover:scale-110 active:scale-95 shadow-lg"
                style={{ backgroundColor: "rgba(0,0,0,0.4)", color: "#fff" }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Name + price gradient overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 pt-10 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                <h3 className="text-white text-base sm:text-lg font-bold leading-tight drop-shadow-md line-clamp-1">
                  {product.name}
                </h3>
                <div className="text-white/80 text-sm font-medium mt-0.5">
                  UGX {price.toLocaleString()}
                </div>
              </div>
            </div>

            {/* ── Selection panels ──────────────────────────────────────── */}
            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5 pb-2">

              {/* Size */}
              {availableSizes.length > 0 && (
                <div>
                  <label className="text-xs font-semibold mb-2 block text-gray-800">Size</label>
                  <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                    {availableSizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={cn(
                          "py-1.5 sm:py-2 px-2 sm:px-3 text-xs font-medium rounded-lg border-2 transition-all hover:scale-105 active:scale-95",
                          selectedSize === size
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-gray-800 border-gray-200 hover:border-gray-400"
                        )}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Design */}
              {availableDesigns.length > 0 && (
                <div>
                  <label className="text-xs font-semibold mb-2 block text-gray-800">Design / Pattern</label>
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                    {availableDesigns.map((design) => (
                      <button
                        key={design.id}
                        onClick={() => setSelectedDesign(design.name)}
                        className={cn(
                          "py-1.5 sm:py-2 px-2 sm:px-3 text-xs font-medium rounded-lg border-2 transition-all hover:scale-105 active:scale-95 text-left flex items-center justify-between",
                          selectedDesign === design.name
                            ? "bg-black text-white border-black"
                            : "bg-transparent text-gray-800 border-gray-200 hover:border-gray-400"
                        )}
                      >
                        <span className="truncate mr-2">{design.name}</span>
                        {selectedDesign === design.name && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Colour */}
              {availableColors.length > 0 && (
                <div>
                  <label className="text-xs font-semibold mb-2 block text-gray-800">Colour</label>
                  <div className="grid grid-cols-5 gap-2 sm:gap-2.5">
                    {availableColors.map((color, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => setSelectedColor(color.name)}
                          title={color.name}
                          className={cn(
                            "w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 transition-all hover:scale-110 active:scale-95 shadow-md",
                            selectedColor === color.name
                              ? "border-black ring-2 ring-black ring-offset-2"
                              : "border-gray-200"
                          )}
                          style={
                            color.isMulticolor
                              ? { background: `conic-gradient(${(color.multicolorList || [color.hex, "#fff"]).join(", ")})` }
                              : { backgroundColor: color.hex }
                          }
                        />
                        <span className={cn(
                          "text-[9px] sm:text-[10px] text-center capitalize line-clamp-1 w-full",
                          selectedColor === color.name ? "text-black font-semibold" : "text-gray-500"
                        )}>
                          {color.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hint: "please select X to continue" */}
              {!hasAllRequired && (
                <div className="rounded-xl p-3 sm:p-4 border border-gray-100 bg-gray-50 text-center">
                  <p className="text-sm font-medium text-gray-500">
                    {(() => {
                      const missing: string[] = [];
                      if (availableSizes.length > 0 && !selectedSize) missing.push("size");
                      if (availableColors.length > 0 && !selectedColor) missing.push("colour");
                      if (availableDesigns.length > 0 && !selectedDesign) missing.push("design");
                      if (missing.length === 1) return `Please select a ${missing[0]} to continue.`;
                      if (missing.length === 2) return `Please select ${missing[0]} and ${missing[1]} to continue.`;
                      return "Please select size, colour and design to continue.";
                    })()}
                  </p>
                </div>
              )}

              {/* Stock + quantity — only after all required options chosen */}
              {hasAllRequired && (
                <div className="rounded-xl p-3 sm:p-4 border border-gray-100 bg-gray-50">

                  {/* Stock indicator */}
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-500">Available Stock</span>
                    <div className="flex items-center gap-2">
                      {isLoadingStock ? (
                        <span className="text-sm text-gray-400">Loading…</span>
                      ) : (
                        <>
                          <div className={cn(
                            "w-2 h-2 rounded-full",
                            currentStock === 0 ? "bg-red-500" : currentStock <= 5 ? "bg-orange-500" : "bg-green-500"
                          )} />
                          <span className={cn(
                            "text-base font-bold",
                            currentStock === 0 ? "text-red-500"
                              : currentStock <= 5 ? "text-orange-500"
                              : "text-green-600"
                          )}>
                            {currentStock === 0
                              ? "Out of Stock"
                              : currentStock <= 5
                              ? `Only ${currentStock} left`
                              : `${currentStock} units`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Quantity stepper — only when in stock */}
                  {currentStock > 0 && (
                    <>
                      <label className="text-sm font-medium mb-2 sm:mb-3 block text-gray-500">Quantity</label>
                      <div className="flex items-center justify-center gap-3 sm:gap-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          disabled={quantity <= 1}
                          className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg border-gray-200"
                          style={{ opacity: quantity <= 1 ? 0.5 : 1 }}
                        >
                          <Minus className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Button>

                        <div className="relative min-w-[4rem] sm:min-w-[5rem]">
                          <input
                            type="number"
                            value={quantity === 0 ? "" : quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (isNaN(val)) { setQuantity(0); return; }
                              if (val > currentStock) {
                                setQuantity(currentStock);
                                toast.error(`Only ${currentStock} available`);
                              } else {
                                setQuantity(Math.max(0, val));
                              }
                            }}
                            onBlur={() => { if (quantity === 0) setQuantity(1); }}
                            className="w-full text-center py-1.5 sm:py-2 px-2 rounded-lg border-2 border-black text-xl sm:text-2xl font-bold outline-none bg-white text-gray-900"
                            style={{ MozAppearance: "textfield" } as any}
                          />
                        </div>

                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setQuantity((q) => Math.min(currentStock, q + 1))}
                          disabled={quantity >= currentStock}
                          className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg border-gray-200"
                          style={{ opacity: quantity >= currentStock ? 0.5 : 1 }}
                        >
                          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Sticky bottom CTA ─────────────────────────────────────── */}
          <div className="shrink-0 p-3 sm:p-4 pt-2 sm:pt-3 border-t border-gray-100 bg-white rounded-b-2xl">
            <Button
              onClick={handleAddToCart}
              disabled={!canAdd}
              className={cn(
                "w-full h-11 sm:h-12 text-sm sm:text-base font-semibold rounded-xl transition-all",
                canAdd
                  ? "bg-black hover:bg-black/90 text-white hover:scale-[1.02] active:scale-[0.98]"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
              )}
            >
              {getButtonLabel()}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
